import {
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
} from "@mui/material";
import { FORM_ERROR } from "final-form";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import React, { useCallback, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  CertificateSkuExtended,
  CertificateSkuRequest,
  ICertificateSkuRequest,
  ListRequestOfBaseFilter,
  ListResponseOfCertificateSkuExtended,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import CertificateSkuForm from "./CertificateSkuForm";

type CertificateSkuFormProps = {
  certificateId: string;
};

interface CertificateSkuExtendedWithId extends CertificateSkuExtended {
  id: string;
}

const CertificateSkusList = ({ certificateId }: CertificateSkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { certificateService } = useGlobalContext();
  const [deleting, setDeleting] = useState<CertificateSkuExtended | null>(null);
  const [editing, setEditing] = useState<CertificateSkuExtended | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);

  const {
    data: certificateSkuData,
    isFetching: isFetchingCertificateSkus,
  } = useQuery<ListResponseOfCertificateSkuExtended>(
    ["certificateSkus", certificateId],
    () =>
      certificateService.getCertificateSkus(
        certificateId,
        ListRequestOfBaseFilter.fromJS({
          sortField: "createdOn",
          sortDescending: false,
        })
      ),
    {
      enabled: !!certificateId,
    }
  );

  const certificateSkus = (certificateSkuData?.data ??
    []) as CertificateSkuExtendedWithId[];

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) {
      try {
        await registerPromise(certificateService.deleteCertificateSku(id));
        queryClient.invalidateQueries(["certificateSkus", certificateId]);

        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "certificateSkuDeleted" }),
            Severity.Warning
          )
        );

        deleteOpenActions.setFalse();
      } catch (err: any) {
        return { [FORM_ERROR]: translateError(err, t) };
      }
    }
  }, [
    certificateId,
    certificateService,
    deleteOpenActions,
    deleting?.id,
    queryClient,
    registerPromise,
    t,
  ]);

  const DeleteButton = ({ value }: { value: CertificateSkuExtended }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => {
            setDeleting(value);
            deleteOpenActions.setTrue();
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const EditButton = ({ value }: { value: CertificateSkuExtended }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => {
            setEditing(value);
            createOpenActions.setTrue();
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "sku",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "certificateSkuSkuLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: EditButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const onSubmit = async (values: ICertificateSkuRequest) => {
    try {
      const response = await registerPromise(
        certificateService.saveCertificateSku(
          certificateId,
          new CertificateSkuRequest(values)
        )
      );

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "certificateSkuCreateMessage" }))
      );

      queryClient.invalidateQueries(["certificateSkus", certificateId]);
      createOpenActions.setFalse();

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <Container>
      <Box mb={3}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box textAlign="right">
              <Button
                variant="outlined"
                type="submit"
                color="primary"
                onClick={() => {
                  setEditing(null);
                  createOpenActions.setTrue();
                }}
                data-cy="addCertificateButton"
              >
                <T id="certificateSkuCreateButtonLabel" />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SortableTable
        {...{
          rows: certificateSkus,
          loading: isFetchingCertificateSkus,
          dense: true,
          cells,
        }}
      />

      <DeleteEntityDialog
        dialogTitleKey={"certificateSkuDeleteTitle"}
        contentKey={"certificateSkuDeleteQuestion"}
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <CertificateSkuForm
          certificateSkuId={editing?.id}
          entity={editing}
          certificateId={certificateId}
          onCancel={createOpenActions.setFalse}
          onSubmit={onSubmit}         
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default CertificateSkusList;
